import { createContext, useCallback, useContext, useState } from "react";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);
  const [inputVal, setInputVal] = useState("");

  const confirm = useCallback((opts) => new Promise((resolve) => {
    setState({ mode: "confirm", ...opts, resolve });
  }), []);

  const prompt = useCallback((opts) => new Promise((resolve) => {
    setInputVal(opts.defaultValue != null ? String(opts.defaultValue) : "");
    setState({ mode: "prompt", ...opts, resolve });
  }), []);

  const settle = (val) => setState((s) => { if (s) s.resolve(val); return null; });

  const isPrompt = state?.mode === "prompt";

  return (
    <ConfirmContext.Provider value={{ confirm, prompt }}>
      {children}
      <AlertDialog open={!!state} onOpenChange={(o) => { if (!o) settle(isPrompt ? null : false); }}>
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{state?.title || "Confirmer"}</AlertDialogTitle>
            {state?.description && <AlertDialogDescription>{state.description}</AlertDialogDescription>}
          </AlertDialogHeader>
          {isPrompt && (
            <div className="space-y-1.5">
              {state?.inputLabel && <Label>{state.inputLabel}</Label>}
              <Input
                data-testid="confirm-input"
                autoFocus
                type={state?.inputType || "text"}
                value={inputVal}
                onChange={(e) => setInputVal(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") settle(inputVal); }}
              />
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel" onClick={() => settle(isPrompt ? null : false)}>
              {state?.cancelText || "Annuler"}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-ok"
              onClick={() => settle(isPrompt ? inputVal : true)}
              className={state?.danger ? "bg-rose-600 hover:bg-rose-700" : ""}
            >
              {state?.confirmText || "Confirmer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export const useConfirm = () => useContext(ConfirmContext);
